import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import Button from "@/ui/Button/Button";
import { ChevronIcon, SendIcon } from "@/assets/icons";
import { DashboardReferralDriver } from "@/types/referals";
import { DAYS, ORDERS, formatRubles, pluralize } from "@/utils/helpers";
import { formatFull } from "@/components/PeriodSelector/periodDates";
import { openTelegramUrl, telegramChatLink } from "@/utils/telegram";
import {
  GOAL_ORDERS,
  GoalRail,
  isWindowExpired,
} from "@/components/GoalsFunnel/GoalRail";
import { ReferralDetailsSkeleton } from "./ReferralDetailsSkeleton";
import cls from "./styles.module.scss";

// «2026-08-03» / ISO с бэка → «3 августа 2026»; пусто — значит данных нет
const formatDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : formatFull(date);
};

interface DetailRowProps {
  label: string;
  value?: string | null;
  // подсвечиваем золотым ненулевые суммы
  gold?: boolean;
}

const DetailRow = ({ label, value, gold }: DetailRowProps) => (
  <div className={cls.row}>
    <span>{label}</span>
    <b
      className={classNames(
        gold && cls.rowValueGold,
        !value && cls.rowValueEmpty
      )}
    >
      {value || "ещё нет"}
    </b>
  </div>
);

const ReferralCard = ({ driver }: { driver: DashboardReferralDriver }) => {
  const isExpired = isWindowExpired(driver);
  const isDone = driver.orders_30.completed;

  const accrued = Number(driver.total_accrued) || 0;
  const potential = Number(driver.potential.known_amount) || 0;

  // ник приходит и с собакой, и без неё — в ссылку нужен чистый
  const username = driver.telegram?.replace(/^@/, "") || null;
  const chatLink = telegramChatLink({
    username,
    phone_number: driver.phone,
  });

  let windowState: string;
  if (isExpired) windowState = "окно закрылось";
  else if (isDone) windowState = "обе цели закрыты";
  else
    windowState = `осталось ${driver.days_remaining} ${pluralize(
      driver.days_remaining,
      DAYS
    )}`;

  return (
    <>
      <div className={cls.card}>
        <div>
          <div className={cls.name}>{driver.name}</div>
          <div className={cls.desc}>
            В парке с {formatDate(driver.registered_at) ?? "—"}
          </div>
        </div>

        <div className={cls.chips}>
          <div className={cls.chip}>{driver.service_display}</div>
          <div className={cls.chip}>{driver.tariff_display}</div>
          {driver.phone && (
            <div className={cls.contactChip}>{driver.phone}</div>
          )}
          {username && <div className={cls.contactChip}>@{username}</div>}
        </div>

        <div>
          <div className={cls.ordersHead}>
            <div className={cls.ordersTitle}>
              {driver.orders_in_window} из {GOAL_ORDERS}{" "}
              {pluralize(GOAL_ORDERS, ORDERS)}
            </div>
            <div className={cls.ordersMeta}>{windowState}</div>
          </div>

          {/* пустая шкала ничего не говорит — рисуем её только с заказами */}
          {driver.orders_in_window > 0 && <GoalRail referral={driver} />}
        </div>

        <div className={cls.list}>
          <DetailRow
            label="Сервис и тариф"
            value={`${driver.service_display} · ${driver.tariff_display}`}
          />
          <DetailRow
            label="Дата регистрации"
            value={formatDate(driver.registered_at)}
          />
          <DetailRow
            label="Заказов за 28 дней"
            value={String(driver.orders_in_window)}
          />
          <DetailRow
            label="Выполнил 5 заказов"
            value={
              driver.orders_5.completed
                ? formatDate(driver.orders_5.completed_at)
                : null
            }
          />
          <DetailRow
            label={`Выполнил ${GOAL_ORDERS} заказов`}
            value={
              driver.orders_30.completed
                ? formatDate(driver.orders_30.completed_at)
                : null
            }
          />
          <DetailRow
            label={isExpired ? "Окно закрылось" : "Окно закроется"}
            value={isDone ? "—" : formatDate(driver.deadline)}
          />
          <DetailRow
            label="Начислено"
            value={formatRubles(driver.total_accrued)}
            gold={accrued > 0}
          />
          <DetailRow
            label="Ещё можно получить"
            value={formatRubles(driver.potential.known_amount)}
            gold={potential > 0}
          />
        </div>
      </div>

      <div className={cls.actions}>
        <Button
          trigger="ref.referral_details.message"
          variant="yellow"
          label="Написать в Telegram"
          icon={<SendIcon />}
          disabled={!chatLink}
          onClick={() => chatLink && openTelegramUrl(chatLink)}
        />
      </div>
    </>
  );
};

interface ReferralDetailsProps {
  // данные приходят отдельным запросом, пока его нет — показываем скелетон
  driver: DashboardReferralDriver | null;
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export const ReferralDetails = ({
  driver,
  isLoading,
  isOpen,
  onClose,
}: ReferralDetailsProps) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    document.body.style.overflow = "hidden";

    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={cls.overlay}
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.28 }}
        >
          <div className={cls.appbar}>
            <button
              type="button"
              data-trigger="ref.referral_details.back"
              className={cls.backButton}
              onClick={onClose}
            >
              <ChevronIcon direction="left" size={18} />
            </button>

            <div>
              <div className={cls.appbarTitle}>Реферал</div>
              <div className={cls.appbarSubtitle}>все данные</div>
            </div>
          </div>

          {isLoading || !driver ? (
            <ReferralDetailsSkeleton />
          ) : (
            <ReferralCard driver={driver} />
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
